import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { TaskContextSearchHit, TaskMember } from '@shared/types/tasks';
import { TASK_CONTEXT_TYPE_LABELS } from '@shared/task-contexts';
import { activeTagQuery, tagRanges } from '@shared/task-tags';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { searchTaskContexts } from '@/lib/tasks-api';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { cn } from '@/lib/utils';
import { CONTEXT_ICONS, memberHandle, memberName } from './task-ui';
import { MemberAvatar } from '@/components/team/MemberAvatar';
import { activeMentionQuery, mentionRanges, type PickedMention } from './mention-text';
import { clipboardFiles } from './clipboard-files';
import { useProjectTags } from './useTasks';
import { AI_BUILDER_PICK, aiBuilderMachinePick, mentionSuggestions, useAiBuilderMentions, type MentionSuggestion } from './ai-builder-mention';
import { useAiMachines } from './use-ai-machines';
import { Server } from 'lucide-react';
import { AiBuilderAvatar } from './task-chips';
import { TASK_AI_BUILDER_NAME } from '@shared/types/tasks';

type Suggestion = MentionSuggestion;

export interface MentionTextareaHandle {
  focus: () => void;
  /** Put text in at the caret (over any selection), e.g. a picked emoji, and focus the box. */
  insertText: (text: string) => void;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  mentions: PickedMention[];
  onMentionsChange: (mentions: PickedMention[]) => void;
  members: TaskMember[];
  /** What the box is for ("Message", "Description"): its name for screen readers, as it has no visible label. */
  'aria-label': string;
  placeholder?: string;
  rows?: number;
  className?: string;
  /** On the textarea, e.g. a maxHeight worked out from the screen (the highlight layer follows its size). */
  style?: CSSProperties;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Cmd/Ctrl+Enter. */
  onSubmit?: () => void;
  /** Focus left the box (not counting picking from the @ list). */
  onBlur?: () => void;
  /** Escape with no @ list open. When given, Escape doesn't also close a surrounding dialog. */
  onEscape?: () => void;
  /** A title-style box: no line breaks, and Enter (with no @ list open) calls onSubmit. */
  singleLine?: boolean;
  /**
   * Chat-style: Enter (with no @ list open) calls onSubmit and Shift+Enter makes a new line. On
   * touch screens, which have no Shift, Enter still makes a new line and the send button sends.
   */
  enterSends?: boolean;
  /** Files pasted (screenshots) or dropped into the box. */
  onFiles?: (files: File[]) => void;
  /** `#` offers the project's tags (task text); off elsewhere. */
  tagging?: boolean;
  /** `@` also offers the AI builder. Defaults to the task's (AiBuilderMentions). */
  aiBuilder?: boolean;
  /**
   * SF-T-174: `@` also offers "AI builder on <machine>", which sets the task's machine: only in a comment (new
   * or edited), where the server reads it. Off elsewhere (a description), where it would do nothing.
   */
  aiMachines?: boolean;
}

/**
 * A textarea where `@` opens one picker for people and factory things (the universal search).
 * People come from the project's members; things come from /tasks/context-search.
 */
export const MentionTextarea = forwardRef<MentionTextareaHandle, MentionTextareaProps>(function MentionTextarea(
  {
    value,
    onChange,
    mentions,
    onMentionsChange,
    members,
    'aria-label': ariaLabel,
    placeholder,
    rows = 3,
    className,
    style,
    autoFocus,
    disabled,
    onSubmit,
    onBlur,
    onEscape,
    singleLine,
    enterSends,
    onFiles,
    tagging = false,
    aiBuilder,
    aiMachines: offerMachines = false,
  },
  ref
) {
  const aiOn = useAiBuilderMentions(aiBuilder);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [focused, setFocused] = useState(false);
  // Closed for this @ (Escape, or a sentence carried on past "No matches"). Stays closed while
  // the query grows; backspacing below its length opens it again.
  const [dismissed, setDismissed] = useState<{ start: number; length: number } | null>(null);
  const [hits, setHits] = useState<TaskContextSearchHit[]>([]);
  const [searchedFor, setSearchedFor] = useState('');
  /** Enter pressed while results were still loading: pick the first one when they arrive. */
  const pendingPick = useRef(false);

  // After a pick or a whole-mention delete, the caret moves as soon as the new text is in the
  // box, before the next key press can land at the old spot.
  const pendingCaret = useRef<number | null>(null);
  const moveCaretTo = (position: number) => {
    pendingCaret.current = position;
    setCaret(position);
  };

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    insertText: (text: string) => {
      const el = textareaRef.current;
      if (!el) return;
      // A blurred textarea keeps its selection, so this lands where the caret was.
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      onChange(el.value.slice(0, start) + text + el.value.slice(end));
      moveCaretTo(start + text.length);
    },
  }));
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (pendingCaret.current === null || !el) return;
    el.focus();
    el.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  }, [value]);

  // Opening an existing text to edit: put the caret at the end, ready to add to it.
  useEffect(() => {
    const el = textareaRef.current;
    if (!autoFocus || !el) return;
    el.setSelectionRange(el.value.length, el.value.length);
    el.scrollTop = el.scrollHeight;
    setCaret(el.value.length);
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => {
    const found = activeMentionQuery(value, caret, mentions);
    if (!found) return null;
    if (dismissed && found.start === dismissed.start && found.query.length >= dismissed.length) return null;
    return found;
  }, [value, caret, dismissed, mentions]);
  const debouncedQuery = useDebouncedValue(active?.query ?? '', 250);

  // `#`: the project's tags, or a new one. An @ being typed takes precedence.
  const activeTag = useMemo(() => {
    if (!tagging || active) return null;
    const found = activeTagQuery(value, caret);
    if (!found) return null;
    if (dismissed && found.start === dismissed.start && found.query.length >= dismissed.length) return null;
    return found;
  }, [tagging, active, value, caret, dismissed]);
  // Asked for only once a `#` is being typed (SF-T-172), then cached for the project.
  const projectTags = useProjectTags(focused && activeTag !== null);
  const tagOptions = useMemo(() => {
    if (!activeTag) return [];
    const q = activeTag.query.replace(/[-_]+$/, '');
    const known = projectTags
      .map((t) => t.tag)
      .filter((tag) => tag.includes(q))
      .sort((a, b) => Number(!a.startsWith(q)) - Number(!b.startsWith(q)))
      .slice(0, 8);
    const fresh = q && /[a-z]/.test(q) && !known.includes(q) ? [q] : [];
    return [...known.map((tag) => ({ tag, fresh: false })), ...fresh.map((tag) => ({ tag, fresh: true }))];
  }, [activeTag, projectTags]);
  const tagOpen = focused && activeTag !== null && tagOptions.length > 0;

  useEffect(() => {
    let cancelled = false;
    if (!active || debouncedQuery.trim().length < 2) {
      setHits([]);
      setSearchedFor(debouncedQuery);
      return undefined;
    }
    void searchTaskContexts(debouncedQuery).then((res) => {
      if (cancelled) return;
      setHits(res.data?.results ?? []);
      setSearchedFor(debouncedQuery);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, active !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // SF-T-174: "AI builder on <machine>", once an @ is typed where the AI builder is offered.
  const { machines: aiMachines } = useAiMachines(offerMachines && aiOn && focused && active !== null);
  const suggestions: Suggestion[] = useMemo(
    () => (active ? mentionSuggestions(active.query, members, hits, aiOn, offerMachines ? (aiMachines ?? []) : []) : []),
    [active, members, hits, aiOn, offerMachines, aiMachines]
  );

  useEffect(() => {
    setHighlight(0);
    pendingPick.current = false;
  }, [active?.query, activeTag?.query]);

  const searching = active !== null && active.query.trim().length >= 2 && searchedFor !== active.query;
  const current = Math.min(highlight, Math.max(0, (tagOpen ? tagOptions.length : suggestions.length) - 1));

  const pick = (s: Suggestion) => {
    if (!active) return;
    // People go in by handle, else by name; SF-T-174: the AI builder on a machine as "AI builder on jackalope".
    const picked: PickedMention =
      s.kind === 'user'
        ? { label: memberHandle(s.member), type: 'user', id: s.member.user_id }
        : s.kind === 'ai'
          ? s.machine
            ? aiBuilderMachinePick(s.machine)
            : AI_BUILDER_PICK
          : { label: s.hit.title, type: s.hit.type, id: s.hit.id };
    const label = picked.label.replace(/[[\]\n]/g, ' ').trim();
    const after = value.slice(caret);
    // Only add a space when the next character isn't one already (re-picking mid-sentence).
    const spacer = /^\s/.test(after) ? '' : ' ';
    const next = `${value.slice(0, active.start)}@${label}${spacer}${after}`;
    onChange(next);
    onMentionsChange([...mentions.filter((m) => m.label !== label), { ...picked, label }]);
    const nextCaret = active.start + label.length + 1 + spacer.length;
    moveCaretTo(nextCaret);
  };

  const pickTag = (tag: string) => {
    if (!activeTag) return;
    const after = value.slice(caret);
    const spacer = /^\s/.test(after) ? '' : ' ';
    onChange(`${value.slice(0, activeTag.start)}#${tag}${spacer}${after}`);
    moveCaretTo(activeTag.start + tag.length + 1 + spacer.length);
  };

  // Keep the highlighted option in view when arrowing through a long list.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [current]);

  // The list shows while typing an @: results, "Searching…", or "No matches".
  const open = focused && active !== null && (suggestions.length > 0 || searching || active.query.length > 0);

  // The last query that found nothing. Typing a space after it means they've carried on with
  // the sentence, so the list closes (even if they type on before the next search lands).
  const [noMatch, setNoMatch] = useState<{ start: number; query: string } | null>(null);
  useEffect(() => {
    if (active && !searching && !suggestions.length && active.query.trim().length >= 2) {
      setNoMatch({ start: active.start, query: active.query });
    }
  }, [active, searching, suggestions.length]);
  useEffect(() => {
    if (!active || !noMatch || noMatch.start !== active.start || !active.query.startsWith(noMatch.query)) return;
    const carriedOn = active.query.slice(noMatch.query.length).search(/\s/);
    if (carriedOn >= 0) setDismissed({ start: active.start, length: noMatch.query.length + carriedOn + 1 });
  }, [active, noMatch]);

  useEffect(() => {
    if (!pendingPick.current || searching) return;
    pendingPick.current = false;
    if (suggestions.length) pick(suggestions[0]);
    // pick is recreated each render; only the arrival of results should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, suggestions]);

  /** Backspace right after a mention removes all of it, like Slack. */
  const deleteMentionBefore = (el: HTMLTextAreaElement): boolean => {
    if (el.selectionStart !== el.selectionEnd) return false;
    const range = mentionRanges(value, mentions).find((r) => r.end === el.selectionStart);
    if (!range) return false;
    onChange(value.slice(0, range.start) + value.slice(range.end));
    moveCaretTo(range.start);
    return true;
  };

  // Escape closes the @ list, else calls onEscape. A surrounding Radix dialog listens for Escape
  // on the document before the textarea sees it, so it's caught earlier, on the window.
  const escape = useRef<() => boolean>(() => false);
  escape.current = () => {
    if (open && active) {
      setDismissed({ start: active.start, length: active.query.length });
      return true;
    }
    if (tagOpen && activeTag) {
      setDismissed({ start: activeTag.start, length: activeTag.query.length });
      return true;
    }
    onEscape?.();
    return Boolean(onEscape);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.target !== textareaRef.current || !escape.current()) return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  // A textarea can't colour words, so a copy of the text is drawn behind it with the picked
  // mentions tinted, and the textarea's own text is made transparent (the caret stays).
  const backdropRef = useRef<HTMLDivElement>(null);
  const highlighted = useMemo(() => {
    const people = mentionRanges(value, mentions).map((r) => ({ ...r, tag: false }));
    const tags = tagging
      ? tagRanges(value)
          .filter((t) => !people.some((m) => t.start < m.end && t.end > m.start))
          .map((t) => ({ start: t.start, end: t.end, tag: true }))
      : [];
    const ranges = [...people, ...tags].sort((a, b) => a.start - b.start);
    if (!ranges.length) return null;
    const parts: ReactNode[] = [];
    let cursor = 0;
    ranges.forEach((r, i) => {
      parts.push(value.slice(cursor, r.start));
      parts.push(
        <mark key={i} className={cn('rounded-sm', r.tag ? 'bg-muted text-foreground' : 'bg-primary/15 text-primary')}>
          {value.slice(r.start, r.end)}
        </mark>
      );
      cursor = r.end;
    });
    // A trailing newline needs something after it to take up its line, as in the textarea.
    parts.push(value.slice(cursor) + (value.endsWith('\n') ? ' ' : ''));
    return parts;
  }, [value, mentions, tagging]);

  return (
    // The list floats in its own layer so a dialog's scrolling box can't cut it off, and it
    // flips above the box when there's no room below (a Board column's quick add, the chat box).
    <Popover open={open || tagOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          {highlighted ? (
            <div
              ref={backdropRef}
              aria-hidden
              className={cn(
                // Same box as the textarea (see components/ui/textarea) so the words line up exactly.
                'pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-base md:text-sm',
                className,
                'border-transparent bg-transparent text-foreground shadow-none'
              )}
            >
              {highlighted}
            </div>
          ) : null}
          <Textarea
            aria-label={ariaLabel}
            ref={textareaRef}
            value={value}
            rows={rows}
            style={style}
            autoFocus={autoFocus}
            disabled={disabled}
            placeholder={placeholder}
            className={cn(
              'resize-y',
              className,
              highlighted && 'relative bg-transparent text-transparent caret-foreground dark:bg-transparent'
            )}
            onScroll={(e) => {
              if (backdropRef.current) backdropRef.current.scrollTop = e.currentTarget.scrollTop;
            }}
            onChange={(e) => {
              onChange(singleLine ? e.target.value.replace(/\n/g, ' ') : e.target.value);
              setCaret(e.target.selectionStart ?? e.target.value.length);
            }}
            onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? 0)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setTimeout(() => setFocused(false), 150);
              onBlur?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && deleteMentionBefore(e.currentTarget)) {
                e.preventDefault();
                return;
              }
              if (tagOpen && activeTag) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const step = e.key === 'ArrowDown' ? 1 : -1;
                  setHighlight((current + step + tagOptions.length) % tagOptions.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  pickTag(tagOptions[current].tag);
                  return;
                }
              }
              if (open && active) {
                const dismiss = () => setDismissed({ start: active.start, length: active.query.length });
                if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && suggestions.length) {
                  e.preventDefault();
                  const step = e.key === 'ArrowDown' ? 1 : -1;
                  setHighlight((current + step + suggestions.length) % suggestions.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  if (suggestions.length) {
                    e.preventDefault();
                    pick(suggestions[current]);
                    return;
                  }
                  if (searching) {
                    e.preventDefault();
                    pendingPick.current = true;
                    return;
                  }
                  // "No matches": close the list; Enter still makes a new line (or submits a one-line box).
                  dismiss();
                  if (e.key === 'Tab') e.preventDefault();
                  if (e.key === 'Tab' || !(singleLine || enterSends)) return;
                }
              }
              if (e.key === 'Enter' && singleLine) {
                e.preventDefault();
                onSubmit?.();
                return;
              }
              if (
                e.key === 'Enter' &&
                enterSends &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing &&
                !window.matchMedia('(pointer: coarse)').matches
              ) {
                e.preventDefault();
                onSubmit?.();
                return;
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onSubmit) {
                e.preventDefault();
                onSubmit();
              }
            }}
            onPaste={(e) => {
              const files = clipboardFiles(e.clipboardData);
              if (files.length && onFiles) {
                e.preventDefault();
                onFiles(files);
              }
            }}
            onDrop={(e) => {
              const files = clipboardFiles(e.dataTransfer);
              if (files.length && onFiles) {
                e.preventDefault();
                onFiles(files);
              }
            }}
          />
        </div>
      </PopoverAnchor>
      {tagOpen ? (
        <PopoverContent
          ref={listRef}
          role="listbox"
          aria-label="Tags"
          side="bottom"
          align="start"
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="max-h-72 w-(--radix-popover-trigger-width) overflow-y-auto p-1 text-sm"
        >
          {tagOptions.map((option, i) => (
            <button
              key={option.tag}
              type="button"
              role="option"
              aria-selected={i === current}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left',
                i === current ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
              )}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pickTag(option.tag)}
            >
              <span className="min-w-0 flex-1 truncate">#{option.tag}</span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {option.fresh ? 'New tag' : `${projectTags.find((t) => t.tag === option.tag)?.count ?? 0} tasks`}
              </span>
            </button>
          ))}
        </PopoverContent>
      ) : open ? (
        <PopoverContent
          ref={listRef}
          role="listbox"
          side="bottom"
          align="start"
          collisionPadding={8}
          // Focus stays in the textarea; typing carries on while the list is open.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="max-h-72 w-(--radix-popover-trigger-width) overflow-y-auto p-1 text-sm"
        >
          {suggestions.map((s, i) => {
            const key = s.kind === 'user' ? `u:${s.member.user_id}` : s.kind === 'ai' ? `ai:${s.machine?.id ?? ''}` : `c:${s.hit.type}:${s.hit.id}`;
            const Icon = s.kind === 'context' ? CONTEXT_ICONS[s.hit.type] : null;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={i === current}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left',
                  i === current ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'
                )}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(s)}
              >
                {s.kind === 'user' ? (
                  <>
                    <MemberAvatar name={memberName(s.member)} photourl={s.member.photourl} size="sm" />
                    <span className="truncate">{memberName(s.member)}</span>
                    {s.member.handle ? <span className="shrink-0 text-xs text-muted-foreground">@{s.member.handle}</span> : null}
                  </>
                ) : s.kind === 'ai' && s.machine ? (
                  <>
                    <Server className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {TASK_AI_BUILDER_NAME} <span className="text-muted-foreground">on {s.machine.name}</span>
                    </span>
                  </>
                ) : s.kind === 'ai' ? (
                  <>
                    <AiBuilderAvatar />
                    <span className="truncate">{TASK_AI_BUILDER_NAME}</span>
                  </>
                ) : (
                  <>
                    {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" /> : null}
                    <span className="min-w-0 flex-1 truncate">
                      {s.hit.title}
                      {s.hit.subtitle ? <span className="text-muted-foreground"> · {s.hit.subtitle}</span> : null}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {TASK_CONTEXT_TYPE_LABELS[s.hit.type]}
                    </span>
                  </>
                )}
              </button>
            );
          })}
          {suggestions.length === 0 ? (
            <div className="px-2 py-1.5 text-muted-foreground">
              {searching
                ? 'Searching…'
                : active && active.query.trim().length < 2
                  ? 'Keep typing to search'
                  : 'No matches'}
            </div>
          ) : null}
        </PopoverContent>
      ) : null}
    </Popover>
  );
});
